import { UseQueryOptions } from '@tanstack/react-query';
import { PaginationState } from '@tanstack/react-table';
import { useQuery, useMutation } from 'data/utils/useQuery';
import { ApplicationControllerService, type ApplicationDto, type ApplicationFilterResponseDto } from 'data/openapi';
import { useDataConfig } from 'data/utils/hooks';
import { QueryKeys } from 'data/constants';

export const useAppList = (
  options?: UseQueryOptions<ApplicationFilterResponseDto, any, ApplicationFilterResponseDto, any>,
  pagination?: PaginationState,
  status?: ApplicationDto.phase,
  name?: string,
) => {
  let from: number | undefined;
  let limit: number | undefined;
  if (pagination) {
    const { pageSize, pageIndex } = pagination as PaginationState;
    from = pageIndex;
    limit = pageSize;
  }
  const { pollingInterval } = useDataConfig();
  return useQuery(
    [QueryKeys.APPLICATIONS, from, limit, status, name],
    () => ApplicationControllerService.listApplications(from && limit ? from * limit : undefined, status, limit, name),
    {
      select: ({ applications, totalCount }) => {
        const list = applications?.map((el) => {
          return {
            value: el.name,
            label: el.name,
            ...el,
          };
        });

        return { applications: list, totalCount };
      },
      refetchInterval: pollingInterval,
      keepPreviousData: true,
      ...options,
    },
  );
};

export const useAppDetails = (name: string, options?: UseQueryOptions<ApplicationDto, any, ApplicationDto, any>) => {
  const { pollingInterval } = useDataConfig();
  return useQuery([QueryKeys.APPLICATIONS, name], () => ApplicationControllerService.getApplication(name), {
    refetchInterval: pollingInterval,
    ...options,
  });
};

export const useDeleteApp = () => {
  return useMutation(async (name: string) => {
    await ApplicationControllerService.deleteApplication(name);
  });
};

export const useCreateApp = () => {
  return useMutation(async (values: ApplicationDto) => {
    await ApplicationControllerService.createApplication(values);
  });
};

export const useUpdateApplication = () => {
  return useMutation(async (values: ApplicationDto) => {
    await ApplicationControllerService.updateApplication(values);
  });
};
